import React, { useState } from "react";
import { FileUploadButton } from "../shared/FileUploadButton";
import { useTranslation } from "react-i18next";
import { MenuCardLabel } from "../menu/MenuCardLabel";
import { MenuCardSubLabel } from "../menu/MenuCardSubLabel";
import { MdOutlineCloudUpload } from "react-icons/md";
import styled from "styled-components";
import { animationLoader } from "../../helper/AnimationLoader";
import { LoadingSpinner } from "../shared/LoadingSpinner";
import { Orientation, PopupSubMenu, PopupSubMenuIconContainer } from "../menu/PopupSubMenu";
import { dataConstants } from "../../../shared/data/dataConstants";
import { MdAddCircle } from "react-icons/md";
import { AnimationNameInputComponent } from "./AnimationNameInputComponent";
import { InvalidCriteriaMessage } from "../shared/InvalidCriteriaMessage";
import { undoableAnimationEditorCreateAnimation } from "../../stores/undo/operation/AnimationEditorCreationOp";
import { toHumanReadableFileSize } from "../../../shared/helper/generalHelpers";
const UploadButton = styled.button.withConfig({
  displayName: "AnimationUploadSubMenu__UploadButton",
  componentId: "sc-122t3li-0"
})(["display:flex;align-items:center;margin-top:10px;width:100%;&:hover{cursor:pointer;}&:disabled{cursor:not-allowed;}"]);
const AddIcon = styled(MdAddCircle).withConfig({
  displayName: "AnimationUploadSubMenu__AddIcon",
  componentId: "sc-122t3li-1"
})(["vertical-align:bottom;font-size:22px;margin-right:4px;"]);
const UploadIcon = styled(MdOutlineCloudUpload).withConfig({
  displayName: "AnimationUploadSubMenu__UploadIcon",
  componentId: "sc-122t3li-2"
})(["cursor:pointer;margin-right:4px;margin-left:2px;"]);
const LoadingIndicatorContainer = styled.div.withConfig({
  displayName: "AnimationUploadSubMenu__LoadingIndicatorContainer",
  componentId: "sc-122t3li-3"
})(["display:flex;flex-direction:column;align-items:center;"]);
export const AnimationUploadSubMenu = () => {
  const {
    t
  } = useTranslation(); // upload data states

  const [selectedSkeletonFile, setSelectedSkeletonFile] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [selectedAtlasFile, setSelectedAtlasFile] = useState(null);
  const [animationName, setAnimationName] = useState(""); // ui component states

  const [currentlyUploading, setCurrentlyUploading] = useState(false);
  const [animationNameValid, setAnimationNameValid] = useState(false);

  async function onClickUpload() {
    setCurrentlyUploading(true);
    const animationAsset = await animationLoader.uploadNewAnimation(animationName, selectedSkeletonFile, selectedImageFile, selectedAtlasFile);
    undoableAnimationEditorCreateAnimation(animationAsset);
    reset();
  }

  function reset() {
    setAnimationName("");
    setCurrentlyUploading(false);
    setSelectedSkeletonFile(null);
    setSelectedImageFile(null);
    setSelectedAtlasFile(null);
    setAnimationNameValid(false);
  }

  return /*#__PURE__*/React.createElement(PopupSubMenu, {
    orientation: Orientation.Right,
    pixelOffsetY: 80,
    relativeOffset: 0.25,
    containerWidth: "180px",
    buttonContent: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PopupSubMenuIconContainer, null, /*#__PURE__*/React.createElement(AddIcon, null)), t("editor.animation_asset_upload_button")),
    onClose: reset
  }, /*#__PURE__*/React.createElement(MenuCardLabel, null, t("editor.animation_asset_upload_create")), currentlyUploading && /*#__PURE__*/React.createElement(LoadingIndicatorContainer, null, /*#__PURE__*/React.createElement(LoadingSpinner, null), t("editor.animation_asset_upload_loading", {
    name: animationName
  })), !currentlyUploading && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MenuCardSubLabel, null, t("editor.animation_asset_upload_select_files")), /*#__PURE__*/React.createElement(FileUploadButton, {
    onFileSelected: file => {
      setSelectedSkeletonFile(file);
      if (!animationName) setAnimationName(file === null || file === void 0 ? void 0 : file.name.replace(/\.[^/.]+$/, ""));
    },
    acceptMimeType: "application/json",
    labelText: t("editor.animation_asset_upload_skeleton")
  }), /*#__PURE__*/React.createElement(FileUploadButton, {
    onFileSelected: setSelectedImageFile,
    acceptMimeType: "image/png",
    labelText: t("editor.animation_asset_upload_image")
  }), /*#__PURE__*/React.createElement(FileUploadButton, {
    onFileSelected: setSelectedAtlasFile,
    acceptMimeType: ".atlas",
    labelText: t("editor.animation_asset_upload_atlas")
  }), (!selectedSkeletonFile || !selectedImageFile || !selectedAtlasFile) && /*#__PURE__*/React.createElement(InvalidCriteriaMessage, null, t("editor.animation_asset_upload_invalid_criteria_select_all_files")), (selectedSkeletonFile ? selectedSkeletonFile.size : 0) + (selectedImageFile ? selectedImageFile.size : 0) + (selectedAtlasFile ? selectedAtlasFile.size : 0) > dataConstants.animationAssetMaxSizeBytes && /*#__PURE__*/React.createElement(InvalidCriteriaMessage, null, t("editor.animation_asset_upload_invalid_upload_sizes", {
    max: toHumanReadableFileSize(dataConstants.animationAssetMaxSizeBytes)
  })), /*#__PURE__*/React.createElement(MenuCardSubLabel, null, t("editor.animation_asset_upload_name")), /*#__PURE__*/React.createElement(AnimationNameInputComponent, {
    setAnimationName: setAnimationName,
    animationName: animationName,
    onNameValidityChange: setAnimationNameValid
  }), /*#__PURE__*/React.createElement(UploadButton, {
    disabled: !selectedSkeletonFile || !selectedImageFile || !selectedAtlasFile || !animationName || !animationNameValid,
    onClick: onClickUpload
  }, /*#__PURE__*/React.createElement(UploadIcon, {
    size: 26
  }), " ", t("editor.animation_asset_upload"))));
};